import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for lazy loading images using IntersectionObserver
 * @param {string} src - The actual image URL to load
 * @param {string} placeholder - Placeholder image (data URI or URL)
 * @returns {Object} - { imageSrc, imageRef, isLoaded }
 */
export function useLazyImage(src, placeholder = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7') {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    // If no src provided, don't do anything
    if (!src) {
      return;
    }

    // Check if IntersectionObserver is supported
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Load the actual image
              setImageSrc(src);
              setIsLoaded(true);
              // Stop observing once loaded
              if (imageRef.current) {
                observer.unobserve(imageRef.current);
              }
            }
          });
        },
        {
          // Start loading when image is 50px away from viewport
          rootMargin: '50px',
        }
      );

      if (imageRef.current) {
        observer.observe(imageRef.current);
      }

      // Cleanup
      return () => {
        if (imageRef.current) {
          observer.unobserve(imageRef.current);
        }
      };
    } else {
      // Fallback for browsers that don't support IntersectionObserver
      setImageSrc(src);
      setIsLoaded(true);
    }
  }, [src, placeholder]);

  return { imageSrc, imageRef, isLoaded };
}

export default useLazyImage;
